import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { invoiceCostLKR } from '../../utils/costPax';
import { nicknameOnly } from '../../utils/clientIdentity';

const STATUS_COLORS = { PAID: '#22c55e', VOID: '#ef4444' };

function calcGP(inv) {
  const sell = (inv.line_items || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const cost = invoiceCostLKR(inv);
  const gp  = sell - cost;
  const pct = sell > 0 ? gp / sell * 100 : 0;
  return { sell, gp, pct };
}

const SORT_ARROW = { asc: ' ▲', desc: ' ▼' };

// Supabase/PostgREST caps an unpaginated select at 1000 rows — with 1100+ invoices, a
// plain .select() silently drops the oldest ~100 (order is created_at desc), making them
// look like they don't exist at all. Same fix already used elsewhere in this codebase
// (e.g. StaffPerformance.js's fetchAllPaged).
const PAGE = 1000;
async function fetchAllPaged(build) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await build().range(from, from + PAGE - 1);
    if (error) throw error;
    if (!data || !data.length) break;
    all = all.concat(data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

export default function InvoiceList() {
  const { role, agentRefId, canSee } = useAuth();
  const canSeeGP = role === 'sadmin' || role === 'admin' || canSee('gp_margins');
  const isLimitedAgent = role === 'agent';
  const [invoices, setInvoices] = useState([]);
  const [receiptsMap, setReceiptsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // Issued-date range (inclusive); either end can be left blank.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortCol, setSortCol] = useState('issued_date');
  const [sortDir, setSortDir] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    // 'invoices' is in the agent role's default tab list (so the sidebar route resolves),
    // but this page otherwise showed every invoice in the company to anyone who reached
    // it — a limited agent has no reason to see clients they don't own, so scope it the
    // same way every other agent-facing query in this codebase already does.
    const data = await fetchAllPaged(() => {
      let q = supabase.from('invoices')
        .select('*, clients!inner(full_name, client_code, care_of_agent_id, agents:care_of_agent_id(tls_code)), pkg:package_id(package_code, package_name)')
        .order('created_at', { ascending: false });
      if (isLimitedAgent) q = q.eq('clients.care_of_agent_id', agentRefId);
      return q;
    });
    // Package invoices raised from a booking carry their package through the booking, not
    // invoices.package_id — look those up so the Type column can name the package.
    const bookingIds = [...new Set((data || []).filter(i => i.source_type === 'booking' && i.source_id && !i.pkg).map(i => i.source_id))];
    if (bookingIds.length) {
      const bks = await fetchAllPaged(() => supabase.from('bookings').select('id, packages(package_code, package_name)').in('id', bookingIds));
      const pkgByBooking = Object.fromEntries((bks || []).filter(b => b.packages).map(b => [b.id, b.packages]));
      for (const inv of data) if (!inv.pkg && pkgByBooking[inv.source_id]) inv.pkg = pkgByBooking[inv.source_id];
    }
    setInvoices(data || []);

    // Paid amount per invoice = new-style receipt_allocations + legacy single-reference
    // receipt_vouchers rows that have not been migrated to allocations (avoids double counting).
    const [allocs, rvs] = await Promise.all([
      fetchAllPaged(() => supabase.from('receipt_allocations').select('receipt_voucher_id, invoice_id, amount').not('invoice_id', 'is', null)),
      fetchAllPaged(() => supabase.from('receipt_vouchers').select('id, reference_id, base_amount, amount').eq('reference_type', 'invoice').is('voided_at', null)),
    ]);
    const allocatedVoucherIds = new Set((allocs || []).map(a => a.receipt_voucher_id));
    const map = {};
    for (const a of (allocs || [])) {
      map[a.invoice_id] = (map[a.invoice_id] || 0) + (parseFloat(a.amount) || 0);
    }
    for (const r of (rvs || [])) {
      if (!r.reference_id || allocatedVoucherIds.has(r.id)) continue;
      map[r.reference_id] = (map[r.reference_id] || 0) + (parseFloat(r.base_amount) || parseFloat(r.amount) || 0);
    }
    setReceiptsMap(map);
    setLoading(false);
  }

  // Type column: the package's own name for package invoices (e.g. "Malaysia - December -
  // 2026 (MH-GRP-01)"), falling back to "Package" for older invoices with no package link.
  function typeLabel(inv) {
    if (inv.source_type === 'booking') return inv.pkg?.package_name || 'Package';
    if (inv.source_type === 'ticket') return 'Ticket';
    if (inv.source_type === 'visa') return 'Visa';
    return inv.source_type || '';
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  function getSortVal(inv, col) {
    switch (col) {
      case 'invoice_number': return inv.invoice_number || '';
      case 'client':         return inv.clients?.full_name || '';
      case 'source_type':    return typeLabel(inv);
      case 'issued_date':    return inv.issued_date || '';
      case 'balance': {
        const total = (inv.line_items || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
        return total - (receiptsMap[inv.id] || 0);
      }
      case 'gp':    return calcGP(inv).gp;
      case 'gp_pct': return calcGP(inv).pct;
      case 'status': return inv.status || '';
      default: return '';
    }
  }

  const filtered = invoices
    .filter(inv => {
      const matchStatus = !statusFilter || inv.status === statusFilter;
      const d = (inv.issued_date || '').slice(0, 10);
      const matchDate = (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
      if (!matchDate) return false;
      if (!q) return matchStatus;
      const qL = q.toLowerCase();
      const matchQ =
        inv.invoice_number?.toLowerCase().includes(qL) ||
        inv.clients?.full_name?.toLowerCase().includes(qL) ||
        inv.clients?.client_code?.toLowerCase().includes(qL) ||
        typeLabel(inv).toLowerCase().includes(qL) ||
        (inv.pkg?.package_code || '').toLowerCase().includes(qL);
      return matchStatus && matchQ;
    })
    .sort((a, b) => {
      // VOIDs always last
      if (a.status === 'VOID' && b.status !== 'VOID') return 1;
      if (a.status !== 'VOID' && b.status === 'VOID') return -1;
      const av = getSortVal(a, sortCol);
      const bv = getSortVal(b, sortCol);
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const thStyle = (col) => ({
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    color: sortCol === col ? 'var(--primary)' : undefined,
  });

  const arrow = (col) => sortCol === col ? SORT_ARROW[sortDir] : '';

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Invoices</h1>
        <Link to="/invoices/new" className="btn btn-primary">+ New Invoice</Link>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="form-input" placeholder="Search invoice #, client, type…" value={q}
          onChange={e => setQ(e.target.value)} style={{ maxWidth: 320 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {[['', 'All'], ['PAID', 'Paid'], ['VOID', 'Void']].map(([v, l]) => (
            <button key={v} className={`btn btn-sm ${statusFilter === v ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setStatusFilter(v)}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          <span>Issued</span>
          <input type="date" className="form-input" style={{ width: 150 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" />
          <span>to</span>
          <input type="date" className="form-input" style={{ width: 150 }} value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" />
          {(dateFrom || dateTo) && (
            <button className="btn btn-sm btn-secondary" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear dates</button>
          )}
        </div>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{filtered.length} invoice{filtered.length === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          {q || statusFilter || dateFrom || dateTo ? 'No invoices match your search.' : 'No invoices yet.'}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={thStyle('invoice_number')} onClick={() => handleSort('invoice_number')}>Invoice #{arrow('invoice_number')}</th>
                <th style={thStyle('client')} onClick={() => handleSort('client')}>Client{arrow('client')}</th>
                <th style={thStyle('source_type')} onClick={() => handleSort('source_type')}>Type{arrow('source_type')}</th>
                <th style={thStyle('issued_date')} onClick={() => handleSort('issued_date')}>Issued{arrow('issued_date')}</th>
                <th style={{ ...thStyle('balance'), textAlign: 'right' }} onClick={() => handleSort('balance')}>Balance Due{arrow('balance')}</th>
                {canSeeGP && <th style={{ ...thStyle('gp'), textAlign: 'right' }} onClick={() => handleSort('gp')}>GP{arrow('gp')}</th>}
                {canSeeGP && <th style={{ ...thStyle('gp_pct'), textAlign: 'right' }} onClick={() => handleSort('gp_pct')}>GP %{arrow('gp_pct')}</th>}
                <th style={thStyle('status')} onClick={() => handleSort('status')}>Status{arrow('status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => {
                const { gp, pct, sell } = calcGP(inv);
                const gpColor = gp >= 0 ? 'var(--success)' : 'var(--danger)';
                const total = (inv.line_items || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
                const paid  = receiptsMap[inv.id] || 0;
                const bal   = total - paid;

                return (
                  <tr key={inv.id} style={{ cursor: 'pointer', opacity: inv.status === 'VOID' ? 0.4 : 1 }} onClick={() => navigate(`/invoices/${inv.id}`)}>
                    <td><strong>{inv.invoice_number}</strong></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        {inv.clients?.agents?.tls_code && <span style={{ color: '#f07040', fontWeight: 600 }}>{inv.clients.agents.tls_code}</span>}
                        <span>{nicknameOnly(inv.clients?.client_code) || inv.clients?.full_name || '—'}</span>
                      </div>
                      {inv.clients?.full_name && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{inv.clients.full_name}</div>}
                    </td>
                    <td>
                      <div>{typeLabel(inv)}</div>
                      {inv.pkg?.package_code && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{inv.pkg.package_code}</div>}
                    </td>
                    <td>{inv.issued_date}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {inv.status === 'VOID' ? <span style={{ color: '#aaa' }}>—</span>
                        : total === 0 ? <span style={{ color: '#aaa' }}>—</span>
                        : <span style={{ color: bal <= 0 ? '#16a34a' : '#111' }}>
                            {bal <= 0 ? 'Paid' : `LKR ${bal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </span>
                      }
                    </td>
                    {canSeeGP && (
                      inv.status === 'VOID' || sell === 0
                        ? <><td style={{ textAlign: 'right' }}><span style={{ color: '#aaa' }}>—</span></td><td style={{ textAlign: 'right' }}><span style={{ color: '#aaa' }}>—</span></td></>
                        : <>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: gpColor }}>{gp.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600, color: gpColor }}>{pct.toFixed(1)}%</td>
                          </>
                    )}
                    <td>
                      {STATUS_COLORS[inv.status] && (
                        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: STATUS_COLORS[inv.status] + '22', color: STATUS_COLORS[inv.status] }}>
                          {inv.status}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Link to={`/invoices/${inv.id}`} style={{ color: 'var(--primary)', fontSize: 13 }} onClick={e => e.stopPropagation()}>View</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
